import blackSpotIcons from '@/components/blackspot/icons.js'
import { distanceInMeters } from '@/utils/geo.js'

export const EVENT_BLACKSPOT_HIGHLIGHT =
  'blackspot_module__blackspot-layer__click'

/**
 * Get blackspot icon src based on constraint image file name (local conversion table)
 * @param {*} constraintId
 * @param {*} constraintsArr
 * @returns
 */
export function getIconSrc(constraintId, constraintsArr) {
  const imageFileName =
    constraintsArr.find((c) => c.id == constraintId)?.image || ''
  return imageFileName
    ? blackSpotIcons[imageFileName.toLowerCase().split('.')[0]]
    : 'https://placehold.co/20x20'
}

/**
 * Extracts latLngs data from a geojsonString
 *
 * @param {string} geojsonString - A string containing geojson information.
 * @returns {array} An array of Leaflet LatLng objects with order [lat, lng].
 */
export function geoJsonToLatLngs(geojsonString) {
  if (geojsonString) {
    // Remove parentheses and split the string into coordinate pairs
    const coordinatePairs = geojsonString
      .slice(1, -1) // Remove parentheses
      .split('):(') // Split into individual coordinate pairs

    // Convert coordinate pairs into Leaflet LatLng objects
    return coordinatePairs.map(function (pair) {
      var separator = pair.includes(',') ? ',' : ':'
      var coords = pair.split(separator)
      var lat = parseFloat(coords[1]) / 100000
      var lng = parseFloat(coords[0]) / 100000
      return [lat, lng] //L.latLng(lat, lng)
    })
  } else {
    return null
  }
}

/**
 * Checks if any of the latLngs in the array are invalid.
 *
 * @param {Array<Array<Number>>} latLngs - The array of latLngs to check.
 * @returns {Boolean} Returns true if any of the latLngs are invalid, otherwise returns false.
 */
export function hasInvalidLatLngs(latLngs) {
  return latLngs.some(
    (latLngItem) =>
      !latLngItem[0] ||
      !latLngItem[1] ||
      (latLngItem[0] || '').toString().includes('NaN') ||
      (latLngItem[1] || '').toString().includes('NaN')
  )
}

/**
 * This function converts latLngs array to GeoJSON String in APIV3 format.
 *
 * @param {*} latLngs Array of [ [0,0] ] or [{lat:0,lng:0}]
 * @returns {String} A GeoJSON string representation of the given coordinates.
 */
export function convertLatLngsToGeoJSONString(latLngs) {
  // Check if nested array (Leaflet stores multipolygons differently than single polygons. A multipolygon can have multiple external rings (equivalent to multiple polygons).)
  if (
    Array.isArray(latLngs[0]) &&
    latLngs[0].length > 0 &&
    !!latLngs[0][0].lat
  ) {
    // Flatten if nested
    latLngs = latLngs.flat()
  }

  return latLngs
    .map((arr) => {
      const lat = arr.lat || arr[0]
      const lng = arr.lng || arr[1]
      return `(${lng * 100000},${lat * 100000})`
    })
    .join(':')
}

/**
 * Creates a manager object allowing for methods to update data from map and bind a layer.
 * The data containing the item (black spot) is updated with geometry related properties such as geoJson, lat, lng, radius.
 * @param {*} updateMapData Handler to update item (black spot) with geometry related properties (geoJson, lat, lng, radius).
 * @param {*} store vuex store
 * @returns {Object} Object containing methods for updating data from map and binding layer.
 */
export function createUpdateDataManager(
  updateMapData = () => {},
  store = {},
  options = {}
) {
  let changeTimeout = null //Prevent multiple calls within 500ms

  const getLayerData = (e = null) => {
    //Circle or Polygon

    const latLng = e
      ? e.layer.getLatLng
        ? e.layer.getLatLng()
        : e.layer.getCenter()
      : null
    const mapBounds = e ? e.layer.getBounds() : null
    const mapData = {}
    mapData.xMin = mapBounds ? mapBounds.getNorthWest().lng : 0
    mapData.yMin = mapBounds ? mapBounds.getNorthWest().lat : 0
    mapData.xMax = mapBounds ? mapBounds.getSouthEast().lng : 0
    mapData.yMax = mapBounds ? mapBounds.getSouthEast().lat : 0
    mapData.lat = latLng?.lat ?? null
    mapData.lng = latLng?.lng ?? null

    //Polygon
    if (e && e.layer.getLatLngs) {
      mapData.geoJson = convertLatLngsToGeoJSONString(e.layer.getLatLngs())
      mapData.shapeName = 'polygone'
    }

    if (e && e.layer.getRadius) {
      mapData.radius = e.layer.getRadius()
      mapData.shapeName = 'cercle'
    } else {
      mapData.radius = 0
    }

    return mapData
  }

  const updateMapDataInternalHandler = (e) => {
    const mapData = getLayerData(e)
    updateMapData(mapData)
    store.state.black_spot.blackspotLayerChangeTimestamp = Date.now()
    console.log({ mapData })
  }

  options.onChangeHandler =
    options.onChangeHandler ||
    function onChangeHandler(mode) {
      //console.log('pm:change (set)', mode)
      return function onChangeHandlerInternal(e) {
        //console.log('pm:change', mode)
        if (!changeTimeout) {
          console.log('pm:change update', mode)
          updateMapDataInternalHandler(mode === 'remove' ? null : e)

          changeTimeout = setTimeout(() => {
            changeTimeout = null //Cooldown
          }, 500)
        }
      }
    }

  return {
    updateDataFromLayer(layer) {
      updateMapDataInternalHandler({ layer })
    },
    /**
     * Map leaflet layer events to a change handler that will update item with geometry related properties
     * @param {*} layer leaflet Layer
     */
    bindLayer(layer) {
      //layer.on('pm:change', onChangeHandler('change'))
      layer.on('pm:edit', options.onChangeHandler('edit'))
      layer.on('pm:update', options.onChangeHandler('update'))
      layer.on('pm:remove', options.onChangeHandler('remove'))
      layer.on('pm:editing:end', options.onChangeHandler('editing:end'))
      layer.on('pm:featureeditend', options.onChangeHandler('featureeditend'))
      layer.on('pm:moveend', options.onChangeHandler('moveend'))
      layer.on('pm:rotateend', options.onChangeHandler('rotateend'))
      layer.on('pm:resizeend', options.onChangeHandler('resizeend'))
    },
  }
}

/**
 * Converts an imported JSON object to a blackspot format.
 *
 * The content of these headers is expected to be string values.
 *
 * @param {Object} json - The imported JSON object.
 * @returns {Object} - A new objec
  const speedLimit = isNaN()
    ? json.vitesse_limite_normale
    : `/geored/black_spot/speed_limits/${json.vitesse_limite_normale}`
t formatted for blackspot representation.
 */
export function convertImportedJsonToBlackspot(json, { speedLimits }) {
  const constraintType = isNaN(json.point_noir_type_contrainte)
    ? json.point_noir_type_contrainte
    : `/geored/black_spot/constraint_types/${json.point_noir_type_contrainte}`

  let item = {
    name: json.name, //Required (Duplicity check)
    description: json.description,
    constraintType: constraintType, //Required
    radius: parseFloat(json.rayon),
    longitude: parseFloat(json.lon),
    latitude: parseFloat(json.lat),
    type: 'Z', //Circle/Polygon = Z, Segment = S (Required)
    shape: 'cercle', //Required if type = Z
  }

  const speed = json.vitesse_limite_normale
  const speedLimitMatch = speedLimits.find((sl) => sl.speed == speed)

  if (speedLimitMatch) {
    item.speedLimit = `/geored/black_spot/speed_limits/${speedLimitMatch.id}`
  }

  return item
}

/*
 * Check against all existing polylines
 * @param {*} existingPolylines
 * @param {*} newPolyline
 * @param {*} errorMargin
 * @returns
 */
export function isNewPolylineConsecutive(
  existingPolylines,
  newPolyline,
  maxGapMeters = 0
) {
  // Get closest endpoints
  let minDistMeters = Infinity
  console.log('Calculating minimum distance (all)...')
  existingPolylines.forEach((existing) => {
    const existingPolylineFirstCoord = getFirstCoord(existing)
    const existingPolylineLastCoord = getLastCoord(existing)

    const newPolylineFirstCoord = getFirstCoord(newPolyline)
    const newPolylineLastCoord = getLastCoord(newPolyline)

    minDistMeters = Math.min(
      minDistMeters,
      distanceInMeters(existingPolylineFirstCoord, newPolylineFirstCoord)
    )
    minDistMeters = Math.min(
      minDistMeters,
      distanceInMeters(existingPolylineFirstCoord, newPolylineLastCoord)
    )
    minDistMeters = Math.min(
      minDistMeters,
      distanceInMeters(existingPolylineLastCoord, newPolylineFirstCoord)
    )
    minDistMeters = Math.min(
      minDistMeters,
      distanceInMeters(existingPolylineLastCoord, newPolylineLastCoord)
    )
  })
  console.log('Minimum distance found:', minDistMeters)

  // Return false if overall gap is too large
  if (minDistMeters > maxGapMeters) {
    console.log('Minimum distance is above threshold', {
      minDistMeters,
      maxGapMeters,
    })
    return false
  }

  console.log('Minimum distance is below threshold (Consecutive)', {
    minDistMeters,
    maxGapMeters,
  })

  return true
}

function getLastCoord(polyline) {
  return polyline.polylineData[polyline.polylineData.length - 1]
}

function getFirstCoord(polyline) {
  return polyline.polylineData[0]
}

/**
 * Computes the total distance in meters between segment parts using the Haversine formula
 * @param {Array<Object>} segmentParts - An array of objects containing polylineData used for computation
 * @returns {Number} Total distance in meters
 */
export function computeTotalDistanceFromSegmentParts(segmentParts) {
  return calculateDistance(segmentParts.map((sp) => sp.polylineData))

  function calculateDistance(points) {
    function haversineDistance(point1, point2) {
      const R = 6371e3 // Radius of earth in meters

      const φ1 = (point1[0] * Math.PI) / 180
      const φ2 = (point2[0] * Math.PI) / 180
      const Δφ = ((point2[0] - point1[0]) * Math.PI) / 180
      const Δλ = ((point2[1] - point1[1]) * Math.PI) / 180

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

      return R * c
    }

    let totalDistance = 0

    for (let polyline of points) {
      let lineDistance = 0

      for (let i = 0; i < polyline.length - 1; i++) {
        lineDistance += haversineDistance(polyline[i], polyline[i + 1])
      }

      totalDistance += lineDistance
    }

    return totalDistance
  }
}
